import { useState, useEffect } from 'react';
import { ClipboardList, Circle, Clock, CheckCircle2, XCircle, Download, Share2, Edit, Trash2, Search, ChevronLeft, ChevronRight, Filter, FileSignature } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import api, { API } from '@/lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import FinalizarOSModal from '@/components/FinalizarOSModal';

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

function Ordens() {
  const navigate = useNavigate();
  const [ordens, setOrdens] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [pecas, setPecas] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);
  const [emitindoNfeId, setEmitindoNfeId] = useState(null);
  const [nfeApta, setNfeApta] = useState(null); // null=carregando, {apta, motivos, ambiente}
  const [finalizarOS, setFinalizarOS] = useState(null); // OS a finalizar (abre modal de pagamento)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingOrdem, setEditingOrdem] = useState(null);
  const [searchPeca, setSearchPeca] = useState('');
  const [veiculosFiltrados, setVeiculosFiltrados] = useState([]);
  
  // Filtros
  const [mesSelecionado, setMesSelecionado] = useState(new Date().getMonth() + 1);
  const [anoSelecionado, setAnoSelecionado] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState('');
  const [mostrarTodos, setMostrarTodos] = useState(false);
  
  const [formData, setFormData] = useState({
    cliente_id: '',
    veiculo_id: '',
    items: [],
    observacoes: ''
  });
  const [currentItem, setCurrentItem] = useState({
    tipo: 'servico',
    descricao: '',
    quantidade: 1,
    valor_unitario: '',
    valor_total: 0
  });

  useEffect(() => {
    loadClientes();
    api.get('/nfe/empresa-apta').then((r) => setNfeApta(r.data)).catch(() => setNfeApta({ apta: false, motivos: ['Não foi possível verificar status NFe'] }));
  }, []);

  useEffect(() => {
    loadOrdens();
  }, [mesSelecionado, anoSelecionado, mostrarTodos]);

  const loadClientes = async () => {
    try {
      const [clientesRes, veiculosRes, pecasRes] = await Promise.all([
        api.get('/clientes'),
        api.get('/veiculos'),
        api.get('/pecas')
      ]);
      setClientes(clientesRes.data);
      setVeiculos(veiculosRes.data);
      setPecas(pecasRes.data);
    } catch (error) {
      console.error(error);
    }
  };

  const loadOrdens = async () => {
    try {
      let url = '/ordens';
      if (!mostrarTodos) {
        url += `?mes=${mesSelecionado}&ano=${anoSelecionado}`;
      }
      const { data } = await api.get(url);
      setOrdens(data);
    } catch (error) {
      toast.error('Erro ao carregar ordens');
      console.error(error);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadOrdens();
      return;
    }
    try {
      const { data } = await api.get(`/ordens?busca=${encodeURIComponent(searchTerm)}`);
      setOrdens(data);
      setMostrarTodos(true);
    } catch (error) {
      toast.error('Erro na busca');
      console.error(error);
    }
  };

  const handleMesAnterior = () => {
    if (mesSelecionado === 1) {
      setMesSelecionado(12);
      setAnoSelecionado(anoSelecionado - 1);
    } else {
      setMesSelecionado(mesSelecionado - 1);
    }
    setMostrarTodos(false);
  };

  const handleMesProximo = () => {
    if (mesSelecionado === 12) {
      setMesSelecionado(1);
      setAnoSelecionado(anoSelecionado + 1);
    } else {
      setMesSelecionado(mesSelecionado + 1);
    }
    setMostrarTodos(false);
  };

  const handleStatusChange = async (ordemId, newStatus) => {
    // Interceptar mudança para "concluido": abrir modal de pagamento
    if (newStatus === 'concluido') {
      const ordem = ordens.find((o) => o.id === ordemId);
      if (ordem) {
        setFinalizarOS(ordem);
        return;
      }
    }
    try {
      await api.put(`/ordens/${ordemId}/status?status=${newStatus}`);
      toast.success('Status atualizado com sucesso');
      loadOrdens();
    } catch (error) {
      toast.error('Erro ao atualizar status');
      console.error(error);
    }
  };

  const handleEdit = (ordem) => {
    setEditingOrdem(ordem);
    const veiculosDoCliente = veiculos.filter(v => v.cliente_id === ordem.cliente_id);
    setVeiculosFiltrados(veiculosDoCliente);
    setFormData({
      cliente_id: ordem.cliente_id,
      veiculo_id: ordem.veiculo_id || '',
      items: ordem.items || [],
      observacoes: ordem.observacoes || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleClienteChange = (clienteId) => {
    const veiculosDoCliente = veiculos.filter(v => v.cliente_id === clienteId);
    setVeiculosFiltrados(veiculosDoCliente);
    setFormData({ ...formData, cliente_id: clienteId, veiculo_id: '' });
  };

  const handleAddItem = () => {
    const valorUnit = parseFloat(currentItem.valor_unitario) || 0;
    const valorTotal = currentItem.quantidade * valorUnit;
    const newItem = { ...currentItem, _key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, valor_unitario: valorUnit, valor_total: valorTotal };
    setFormData({ ...formData, items: [...formData.items, newItem] });
    setCurrentItem({ tipo: 'servico', descricao: '', quantidade: 1, valor_unitario: '', valor_total: 0 });
    setSearchPeca('');
  };

  const handleSelectPeca = (pecaId) => {
    const peca = pecas.find(p => p.id === pecaId);
    if (peca) {
      setCurrentItem({
        tipo: 'peca',
        descricao: peca.nome,
        quantidade: 1,
        valor_unitario: peca.preco_venda,
        valor_total: peca.preco_venda
      });
      setSearchPeca('');
      toast.success(`Peça "${peca.nome}" selecionada`);
    }
  };

  const filteredPecas = pecas.filter(p =>
    searchPeca && (
      p.nome.toLowerCase().includes(searchPeca.toLowerCase()) ||
      p.codigo.toLowerCase().includes(searchPeca.toLowerCase())
    )
  );

  const handleRemoveItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      toast.error('Adicione pelo menos um item');
      return;
    }
    try {
      await api.put(`/ordens/${editingOrdem.id}`, formData);
      toast.success('Ordem de serviço atualizada com sucesso');
      setIsEditDialogOpen(false);
      resetForm();
      loadOrdens();
    } catch (error) {
      toast.error('Erro ao atualizar ordem de serviço');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta ordem de serviço?')) {
      try {
        await api.delete(`/ordens/${id}`);
        toast.success('Ordem de serviço excluída');
        loadOrdens();
      } catch (error) {
        toast.error('Erro ao excluir ordem de serviço');
        console.error(error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      cliente_id: '',
      veiculo_id: '',
      items: [],
      observacoes: ''
    });
    setVeiculosFiltrados([]);
    setCurrentItem({ tipo: 'servico', descricao: '', quantidade: 1, valor_unitario: '', valor_total: 0 });
    setSearchPeca('');
    setEditingOrdem(null);
  };

  const handleEmitirNFe = async (ordem) => {
    // Pré-checagem: se sabemos que não está apta, mostra os motivos formatados
    if (nfeApta && !nfeApta.apta) {
      toast.error(
        `Empresa não está apta para emitir NFe.\n• ${(nfeApta.motivos || []).join('\n• ')}`,
        { duration: 8000 },
      );
      return;
    }
    const cliente = clientes.find(c => c.id === ordem.cliente_id);
    if (!cliente) {
      toast.error('Cliente da OS não encontrado');
      return;
    }
    if (!cliente.cpf_cnpj && !cliente.cpf && !cliente.cnpj) {
      toast.error('Cliente sem CPF/CNPJ — atualize o cadastro antes de emitir NFe');
      return;
    }
    // Valida endereço mínimo do cliente (SEFAZ exige)
    const faltam = [];
    if (!cliente.cidade) faltam.push('Cidade');
    if (!cliente.estado) faltam.push('UF');
    if (!cliente.endereco) faltam.push('Endereço');
    if (!cliente.bairro) faltam.push('Bairro');
    if (faltam.length) {
      toast.error(
        `Cliente "${cliente.nome}" tem cadastro incompleto.\n• Falta: ${faltam.join(', ')}\n\nAtualize em Cadastro → Clientes antes de emitir NFe.`,
        { duration: 9000 },
      );
      return;
    }
    setEmitindoNfeId(ordem.id);
    try {
      const items = (ordem.items || []).map((i) => ({
        descricao: i.descricao || 'Serviço/Peça',
        quantidade: i.quantidade || 1,
        valor_unitario: i.valor_unitario || (i.valor_total / (i.quantidade || 1)),
        ncm: i.ncm || '00000000',
        cfop: i.tipo === 'servico' ? '5933' : '5102',
        unidade: 'UN',
      }));
      await api.post('/nfe/emitir', {
        origem_tipo: 'ordem_servico',
        origem_id: ordem.id,
        destinatario_cpf_cnpj: cliente.cpf_cnpj || cliente.cpf || cliente.cnpj,
        destinatario_nome: cliente.nome,
        destinatario_email: cliente.email,
        destinatario_endereco: cliente.endereco,
        destinatario_numero: cliente.numero,
        destinatario_bairro: cliente.bairro,
        destinatario_cep: cliente.cep,
        destinatario_municipio: cliente.cidade,
        destinatario_uf: cliente.estado,
        natureza_operacao: 'Prestação de Serviço',
        items,
        informacoes_adicionais: `Referente à OS #${ordem.id.slice(-6)}`,
      });
      toast.success('NFe enviada para autorização!');
      navigate('/oficina/nfe');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro ao emitir NFe';
      // Se vier no formato "Empresa não está apta...: A; B; C", quebra em linhas
      const formatted = typeof detail === 'string' && detail.includes(';')
        ? detail.replace(/; /g, '\n• ').replace(': ', ':\n• ')
        : detail;
      toast.error(formatted, { duration: 8000 });
    } finally {
      setEmitindoNfeId(null);
    }
  };

  const handleDownloadRecibo = async (ordem) => {
    setDownloadingId(ordem.id);
    try {
      const response = await api.get(`/ordens/${ordem.id}/recibo`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `recibo_os_${ordem.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Recibo baixado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar recibo');
      console.error(error);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleWhatsAppRecibo = async (ordem) => {
    const cliente = clientes.find(c => c.id === ordem.cliente_id);
    const veiculo = veiculos.find(v => v.id === ordem.veiculo_id);
    
    let mensagem = `*RECIBO DE SERVIÇO*\n\n`;
    mensagem += `*Cliente:* ${cliente?.nome || '-'}\n`;
    if (veiculo) {
      mensagem += `*Veículo:* ${veiculo.marca} ${veiculo.modelo} - ${veiculo.placa}\n`;
    }
    mensagem += `*Data:* ${formatDate(ordem.data_conclusao || ordem.data_inicio)}\n\n`;
    mensagem += `*SERVIÇOS REALIZADOS:*\n`;
    
    ordem.items.forEach((item, index) => {
      mensagem += `${index + 1}. ${item.descricao}\n`;
      mensagem += `   ${item.quantidade}x ${formatCurrency(item.valor_unitario)} = ${formatCurrency(item.valor_total)}\n`;
    });
    
    mensagem += `\n*VALOR TOTAL: ${formatCurrency(ordem.valor_total)}*\n`;
    mensagem += `\n_Acesse o link abaixo para baixar o recibo em PDF:_\n`;
    mensagem += `${API}/ordens/${ordem.id}/recibo`;
    
    const mensagemCodificada = encodeURIComponent(mensagem);
    
    let whatsappUrl;
    if (cliente?.telefone) {
      const telefone = cliente.telefone.replace(/\D/g, '');
      whatsappUrl = `https://wa.me/55${telefone}?text=${mensagemCodificada}`;
    } else {
      whatsappUrl = `https://wa.me/?text=${mensagemCodificada}`;
    }
    
    window.open(whatsappUrl, '_blank');
    toast.success('Abrindo WhatsApp...');
  };

  // Compartilhar via WhatsApp SEM valores (para ordens não concluídas)
  const handleWhatsAppSemValores = (ordem) => {
    const cliente = clientes.find(c => c.id === ordem.cliente_id);
    const veiculo = veiculos.find(v => v.id === ordem.veiculo_id);
    
    let mensagem = `*ORDEM DE SERVIÇO*\n\n`;
    mensagem += `*Cliente:* ${cliente?.nome || '-'}\n`;
    if (veiculo) {
      mensagem += `*Veículo:* ${veiculo.marca} ${veiculo.modelo} - ${veiculo.placa}\n`;
    }
    mensagem += `*Data:* ${formatDate(ordem.data_inicio)}\n`;
    mensagem += `*Status:* ${getStatusLabel(ordem.status)}\n\n`;
    mensagem += `*SERVIÇOS A REALIZAR:*\n`;
    
    ordem.items.forEach((item, index) => {
      mensagem += `${index + 1}. ${item.descricao}`;
      if (item.quantidade > 1) {
        mensagem += ` (${item.quantidade}x)`;
      }
      mensagem += `\n`;
    });
    
    if (ordem.observacoes) {
      mensagem += `\n*Observações:* ${ordem.observacoes}`;
    }
    
    const mensagemCodificada = encodeURIComponent(mensagem);
    
    let whatsappUrl;
    if (cliente?.telefone) {
      const telefone = cliente.telefone.replace(/\D/g, '');
      whatsappUrl = `https://wa.me/55${telefone}?text=${mensagemCodificada}`;
    } else {
      whatsappUrl = `https://wa.me/?text=${mensagemCodificada}`;
    }
    
    window.open(whatsappUrl, '_blank');
    toast.success('Abrindo WhatsApp...');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    if (dateString.length === 10) {
      dateString = dateString + 'T12:00:00';
    }
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getClienteNome = (id) => {
    const cliente = clientes.find(c => c.id === id);
    return cliente ? cliente.nome : '-';
  };

  const getVeiculoInfo = (id) => {
    const veiculo = veiculos.find(v => v.id === id);
    return veiculo ? `${veiculo.marca} ${veiculo.modelo} - ${veiculo.placa}` : '';
  };

  const calcularTotal = () => {
    return formData.items.reduce((acc, item) => acc + item.valor_total, 0);
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'aguardando': return <Circle className="w-4 h-4 text-slate-500" />;
      case 'em_andamento': return <Clock className="w-4 h-4 text-electric-blue" />;
      case 'concluido': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'cancelado': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <Circle className="w-4 h-4 text-slate-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'aguardando': return 'bg-slate-500/10 text-slate-500';
      case 'em_andamento': return 'bg-electric-blue/10 text-electric-blue';
      case 'concluido': return 'bg-green-500/10 text-green-500';
      case 'cancelado': return 'bg-red-500/10 text-red-500';
      default: return 'bg-slate-500/10 text-slate-500';
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'aguardando': return 'Aguardando';
      case 'em_andamento': return 'Em Andamento';
      case 'concluido': return 'Concluído';
      case 'cancelado': return 'Cancelado';
      default: return status;
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="ordens-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white uppercase tracking-tight mb-1">Ordens de Serviço</h1>
          <p className="text-sm text-slate-500">Gerencie as ordens de serviço</p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Seletor de Mês */}
          <div className="flex items-center gap-1 bg-carbon border border-white/10 rounded-md px-2 py-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMesAnterior}
              className="text-slate-400 hover:text-white p-1"
              data-testid="ordens-mes-anterior-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-white font-medium text-sm min-w-[100px] text-center">
              {mostrarTodos ? 'Todos' : `${MESES[mesSelecionado - 1].slice(0, 3)}/${anoSelecionado}`}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMesProximo}
              className="text-slate-400 hover:text-white p-1"
              data-testid="ordens-mes-proximo-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Botão Ver Todos */}
          <Button
            variant={mostrarTodos ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setMostrarTodos(!mostrarTodos);
              setSearchTerm('');
            }}
            className={mostrarTodos ? "bg-electric-blue text-white" : "border-white/10 text-slate-400"}
            data-testid="ver-todos-btn"
          >
            <Filter className="w-4 h-4 mr-1" />
            Todos
          </Button>
        </div>
      </div>

      {/* Barra de Pesquisa */}
      <div className="mb-6">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Buscar por cliente, veículo, placa, serviço..."
              className="bg-carbon border-white/10 text-white pl-10"
              data-testid="search-ordens-input"
            />
          </div>
          <Button
            onClick={handleSearch}
            className="bg-electric-blue hover:bg-blue-600 text-white"
            data-testid="search-ordens-btn"
          >
            <Search className="w-4 h-4" />
          </Button>
          {searchTerm && (
            <Button
              variant="ghost"
              onClick={() => {
                setSearchTerm('');
                loadOrdens();
              }}
              className="text-slate-400"
            >
              Limpar
            </Button>
          )}
        </div>
      </div>

      {/* Dialog de Edição */}
      <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
        setIsEditDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="bg-carbon border-white/10 text-white max-w-[95vw] sm:max-w-2xl lg:max-w-4xl max-h-[85vh] overflow-y-auto mx-2">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold uppercase">Editar Ordem de Serviço</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveEdit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Cliente</Label>
                <Select 
                  value={formData.cliente_id} 
                  onValueChange={handleClienteChange}
                >
                  <SelectTrigger className="bg-obsidian border-white/10 text-white">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent className="bg-carbon border-white/10 text-white">
                    {clientes.map(cliente => (
                      <SelectItem key={cliente.id} value={cliente.id}>{cliente.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Veículo</Label>
                <Select 
                  value={formData.veiculo_id} 
                  onValueChange={(value) => setFormData({ ...formData, veiculo_id: value })}
                  disabled={!formData.cliente_id || veiculosFiltrados.length === 0}
                >
                  <SelectTrigger className="bg-obsidian border-white/10 text-white">
                    <SelectValue placeholder={formData.cliente_id ? "Selecione" : "Cliente primeiro"} />
                  </SelectTrigger>
                  <SelectContent className="bg-carbon border-white/10 text-white">
                    {veiculosFiltrados.map(veiculo => (
                      <SelectItem key={veiculo.id} value={veiculo.id}>
                        {veiculo.placa} - {veiculo.marca} {veiculo.modelo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Busca de Peças */}
            <div className="bg-obsidian p-3 sm:p-4 rounded-md border border-white/10">
              <Label className="text-xs uppercase tracking-wider text-electric-blue mb-2 block">Buscar Peça do Estoque</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  value={searchPeca}
                  onChange={(e) => setSearchPeca(e.target.value)}
                  placeholder="Digite para buscar..."
                  className="bg-carbon border-white/10 text-white pl-10"
                />
              </div>
              {filteredPecas.length > 0 && (
                <div className="mt-2 bg-carbon border border-white/10 rounded-md max-h-32 overflow-y-auto">
                  {filteredPecas.map(peca => (
                    <button
                      key={peca.id}
                      type="button"
                      onClick={() => handleSelectPeca(peca.id)}
                      className="w-full text-left px-3 py-2 hover:bg-white/5 text-sm text-white flex justify-between"
                    >
                      <span>{peca.nome}</span>
                      <span className="text-electric-blue">{formatCurrency(peca.preco_venda)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Adicionar Item Manual */}
            <div className="bg-obsidian p-3 sm:p-4 rounded-md border border-white/10">
              <Label className="text-xs uppercase tracking-wider text-electric-blue mb-2 block">Adicionar Item</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                <Select value={currentItem.tipo} onValueChange={(value) => setCurrentItem({ ...currentItem, tipo: value })}>
                  <SelectTrigger className="bg-carbon border-white/10 text-white text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-carbon border-white/10 text-white">
                    <SelectItem value="servico">Serviço</SelectItem>
                    <SelectItem value="peca">Peça</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  value={currentItem.descricao}
                  onChange={(e) => setCurrentItem({ ...currentItem, descricao: e.target.value })}
                  placeholder="Descrição"
                  className="bg-carbon border-white/10 text-white text-sm col-span-2 sm:col-span-1"
                />
                <Input
                  type="number"
                  value={currentItem.quantidade}
                  onChange={(e) => setCurrentItem({ ...currentItem, quantidade: parseInt(e.target.value) || 1 })}
                  placeholder="Qtd"
                  className="bg-carbon border-white/10 text-white text-sm w-full"
                  min="1"
                />
                <div className="flex gap-2 col-span-2 sm:col-span-1">
                  <Input
                    type="number"
                    step="0.01"
                    value={currentItem.valor_unitario}
                    onChange={(e) => setCurrentItem({ ...currentItem, valor_unitario: e.target.value })}
                    placeholder="Valor"
                    className="bg-carbon border-white/10 text-white text-sm flex-1"
                  />
                  <Button type="button" onClick={handleAddItem} className="bg-electric-blue hover:bg-blue-600 text-white text-sm">
                    +
                  </Button>
                </div>
              </div>
            </div>

            {/* Lista de Itens */}
            {formData.items.length > 0 && (
              <div className="bg-obsidian p-3 sm:p-4 rounded-md border border-white/10">
                <Label className="text-xs uppercase tracking-wider text-slate-400 mb-2 block">Itens Adicionados</Label>
                <div className="space-y-2">
                  {formData.items.map((item, index) => (
                    <div key={item._key || item.id || index} className="flex items-center justify-between bg-carbon p-2 rounded text-sm">
                      <div className="flex-1 min-w-0">
                        <span className={`text-[10px] uppercase px-1 py-0.5 rounded mr-2 ${item.tipo === 'servico' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}`}>
                          {item.tipo}
                        </span>
                        <span className="text-white">{item.descricao}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400 text-xs">{item.quantidade}x</span>
                        <span className="text-white">{formatCurrency(item.valor_total)}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveItem(index)}
                          className="text-red-500 hover:text-red-400 p-1 h-6"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-end pt-2 border-t border-white/10">
                    <span className="text-lg font-bold text-white">Total: {formatCurrency(calcularTotal())}</span>
                  </div>
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Observações</Label>
              <Input
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                className="bg-obsidian border-white/10 text-white"
                placeholder="Opcional"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="ghost" onClick={() => setIsEditDialogOpen(false)} className="text-slate-400">
                Cancelar
              </Button>
              <Button type="submit" className="bg-electric-blue hover:bg-blue-600 text-white">
                Salvar Alterações
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Lista de Ordens */}
      {ordens.length === 0 ? (
        <div className="bg-carbon border border-white/5 rounded-md p-8 sm:p-12 text-center">
          <ClipboardList className="w-10 h-10 sm:w-12 sm:h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-sm sm:text-base text-slate-500">
            {searchTerm ? 'Nenhuma ordem encontrada para essa busca' : `Nenhuma ordem de serviço em ${MESES[mesSelecionado - 1]} ${anoSelecionado}`}
          </p>
          <p className="text-xs sm:text-sm text-slate-600 mt-2">Converta um orçamento aprovado para criar uma OS</p>
        </div>
      ) : (
        <div className="space-y-3">
          {ordens.map((ordem) => {
            const veiculo = veiculos.find(v => v.id === ordem.veiculo_id);
            return (
              <div
                key={ordem.id}
                className="bg-carbon border border-white/5 rounded-md p-4 hover:border-white/20 transition-colors"
                data-testid={`ordem-card-${ordem.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  {/* Info Principal */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {getStatusIcon(ordem.status)}
                      <h3 className="font-semibold text-white text-base truncate">{getClienteNome(ordem.cliente_id)}</h3>
                      <span className={`text-[10px] uppercase px-2 py-0.5 rounded ${getStatusColor(ordem.status)}`}>
                        {getStatusLabel(ordem.status)}
                      </span>
                    </div>
                    {veiculo && (
                      <p className="text-sm text-slate-400 mb-1">
                        {veiculo.marca} {veiculo.modelo} - <span className="text-electric-blue font-medium">{veiculo.placa}</span>
                      </p>
                    )}
                    <p className="text-xs text-slate-500">
                      Criado: {formatDate(ordem.data_inicio)}
                      {ordem.data_conclusao && ` • Concluído: ${formatDate(ordem.data_conclusao)}`}
                    </p>
                    
                    {/* Itens */}
                    <div className="mt-2 space-y-1">
                      {ordem.items.slice(0, 3).map((item, index) => (
                        <div key={item._key || `${ordem.id}-item-${index}`} className="text-xs text-slate-400">
                          • {item.descricao} ({item.quantidade}x {formatCurrency(item.valor_unitario)})
                        </div>
                      ))}
                      {ordem.items.length > 3 && (
                        <div className="text-xs text-slate-500">+{ordem.items.length - 3} mais itens</div>
                      )}
                    </div>
                  </div>
                  
                  {/* Valor e Ações */}
                  <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2">
                    <span className="text-xl font-bold text-white">{formatCurrency(ordem.valor_total)}</span>
                    
                    <div className="flex items-center gap-1">
                      <Select
                        value={ordem.status}
                        onValueChange={(value) => handleStatusChange(ordem.id, value)}
                      >
                        <SelectTrigger className="w-28 h-8 bg-obsidian border-white/10 text-white text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-carbon border-white/10 text-white">
                          <SelectItem value="aguardando">Aguardando</SelectItem>
                          <SelectItem value="em_andamento">Em Andamento</SelectItem>
                          <SelectItem value="concluido">Concluído</SelectItem>
                          <SelectItem value="cancelado">Cancelado</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(ordem)}
                        className="text-slate-400 hover:text-white hover:bg-white/10 p-2 h-8"
                        data-testid={`editar-ordem-${ordem.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(ordem.id)}
                        className="text-red-500 hover:text-white hover:bg-red-500/10 p-2 h-8"
                        data-testid={`delete-ordem-${ordem.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Botão WhatsApp para ordens NÃO concluídas (sem valores) */}
                {ordem.status !== 'concluido' && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleWhatsAppSemValores(ordem)}
                      className="text-green-500 hover:text-white hover:bg-green-500/10 h-8"
                      data-testid={`whatsapp-os-${ordem.id}`}
                    >
                      <Share2 className="w-4 h-4 mr-1" />
                      Compartilhar WhatsApp
                    </Button>
                  </div>
                )}
                
                {/* Botões de Recibo - quando concluído (com valores) */}
                {ordem.status === 'concluido' && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
                    <Button
                      size="sm"
                      onClick={() => handleDownloadRecibo(ordem)}
                      disabled={downloadingId === ordem.id}
                      className="bg-electric-blue hover:bg-blue-600 text-white text-xs h-8"
                      data-testid={`download-recibo-${ordem.id}`}
                    >
                      <Download className="w-3 h-3 mr-1" />
                      {downloadingId === ordem.id ? 'Gerando...' : 'Baixar Recibo PDF'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleWhatsAppRecibo(ordem)}
                      className="text-green-500 hover:text-white hover:bg-green-500/10 h-8"
                      data-testid={`whatsapp-recibo-${ordem.id}`}
                    >
                      <Share2 className="w-4 h-4 mr-1" />
                      WhatsApp
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEmitirNFe(ordem)}
                      disabled={emitindoNfeId === ordem.id}
                      title={nfeApta && !nfeApta.apta ? `Pendências:\n• ${(nfeApta.motivos || []).join('\n• ')}` : 'Emitir Nota Fiscal Eletrônica'}
                      className={
                        nfeApta && !nfeApta.apta
                          ? 'text-slate-500 hover:text-amber-400 hover:bg-amber-500/10 h-8'
                          : 'text-amber-400 hover:text-white hover:bg-amber-500/10 h-8'
                      }
                      data-testid={`emitir-nfe-os-${ordem.id}`}
                    >
                      <FileSignature className="w-4 h-4 mr-1" />
                      {emitindoNfeId === ordem.id ? 'Emitindo...' : (nfeApta && !nfeApta.apta ? 'NFe (pendências)' : 'Emitir NFe')}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <FinalizarOSModal
        open={!!finalizarOS}
        onOpenChange={(o) => !o && setFinalizarOS(null)}
        ordem={finalizarOS}
        onSuccess={() => { setFinalizarOS(null); loadOrdens(); }}
      />
    </div>
  );
}

export default Ordens;
